import {
  ButtonBase,
  Card,
  CardContent,
  Grid,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import {DateTime} from 'luxon';
import React, {useMemo} from 'react';
import {
  getEnergyRatio,
  getEnergyRatioFromFloat,
} from '../../services/measurementService';
import {getTime} from '../../services/dateService';
import SessionTileStat from '../shared/SessionTileStat';
import {isNullOrUndefined} from '../../helpers';
import {useTranslation} from 'react-i18next';
import {SessionTypes} from '../../enums';

interface Props {
  sx: SxProps;
  sessionType?: string;
  active: boolean;
  disabled?: boolean;
  shiftNumber: number;
  drill?: string;
  activeTime?: number;
  energyRatio?: number;
  strideCountTotal?: number;
  onClick: () => void;
  localDisplayStartTime?: string;
  startTime?: string;
}

const PlayerSessionShiftCard: React.FC<Props> = ({
  sx,
  sessionType,
  active,
  disabled,
  shiftNumber,
  drill,
  activeTime,
  energyRatio,
  strideCountTotal,
  onClick,
  localDisplayStartTime,
  startTime,
}) => {
  const {t} = useTranslation();
  const theme = useTheme();

  const displayStartTime = useMemo(() => {
    if (localDisplayStartTime) {
      return DateTime.fromFormat(
        localDisplayStartTime,
        'M/d/yyyy h:mm a',
      ).toFormat('h:mm a');
    }
    if (startTime) {
      return DateTime.fromISO(startTime).toFormat('h:mm a');
    }
    return '';
  }, [startTime, localDisplayStartTime]);

  return (
    <Card
      sx={{
        ...sx,
        ...(disabled ? {opacity: 0.4, pointerEvents: 'none'} : {}),
      }}
      style={
        active
          ? {
              border: 'solid',
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            }
          : undefined
      }>
      <ButtonBase sx={{width: '100%'}} onClick={onClick} disabled={disabled}>
        <CardContent sx={{width: '100%'}}>
          <Typography
            variant="h5"
            sx={{
              textAlign: 'left',
              mb: 1,
            }}>{`${t([SessionTypes.practice, SessionTypes.skills].includes(sessionType as SessionTypes) ? 'shifts.drillRunNumber' : 'shifts.shiftNumber', {number: shiftNumber})} ${
            displayStartTime ? ` - ${displayStartTime}` : ''
          }`}</Typography>
          {drill && (
            <Typography
              variant="h6"
              sx={{
                textAlign: 'left',
                mb: 1,
              }}>
              {t('shifts.shiftDrill', {drill})}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <SessionTileStat
                label={t('shifts.timeAbbrev') as string}
                value={getTime(activeTime)}
              />
            </Grid>
            <Grid item xs={4}>
              {!isNullOrUndefined(energyRatio) && (
                <SessionTileStat
                  label={t('shifts.energyRatioAbbrev') as string}
                  value={
                    getEnergyRatioFromFloat(
                      getEnergyRatio({
                        ratio: energyRatio,
                      }),
                    ) ?? '--'
                  }
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <SessionTileStat
                label={t('shifts.strideCountAbbrev') as string}
                value={strideCountTotal ?? '--'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </ButtonBase>
    </Card>
  );
};

export default React.memo(PlayerSessionShiftCard);
